import { useEffect, useRef } from "react";
import { LinearProgress, Stack, Box, Typography, Divider } from "@mui/material";
import { useChunkedUploadFiles } from "@/components/hooks";

const BYTES_IN_KB = 1024;
const BYTES_IN_MB = 1024 * 1024;

const formatBytes = (bytes) => {
  if (typeof bytes !== "number" || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
};

// Slow-mode uploaded formatter — mirrors UploadProgressLoadingCard.formatKb.
// Kept local instead of imported so this component stays self-contained; the
// spec is one thousands-separated KB literal (no unit picker) so duplicating
// it is cheaper than exposing an internal helper.
const formatKbLoaded = (bytes) =>
  `${Math.floor(Math.max(0, bytes) / BYTES_IN_KB).toLocaleString()} KB`;

// Render the "loaded / total" caption. Asymmetric slow-mode: loaded flips
// to fine-grained KB (visible per-second motion on ~5 KB/s throttled
// networks) while total stays in its natural coarse unit (stable label).
// Only applies when the natural total unit would be MB or GB — a sub-1 MB
// file's total is already displayed in KB, so no need to force it.
const formatLoadedOverTotal = (loaded, total, showUploadedInKb) => {
  const totalIsCoarse = total >= BYTES_IN_MB;
  if (showUploadedInKb && totalIsCoarse) {
    return `${formatKbLoaded(loaded)} / ${formatBytes(total)}`;
  }
  return `${formatBytes(loaded)} / ${formatBytes(total)}`;
};

const STATUS_LABEL = {
  queued: "Queued",
  uploading: "Uploading",
  retrying: "Retrying",
  paused: "Paused",
  uploaded: "Done",
  failed: "Failed",
};

// Compact mode (popup) uses a scrolling container that auto-tracks the currently-uploading
// file. Without auto-track, a card with 20 files would show 5 "Done" rows while the active
// upload happens somewhere off-screen — the user can't tell anything is moving. The
// non-compact path (Uploading page) renders all files and lets the page scroll handle height.
const COMPACT_LIST_MAX_HEIGHT_PX = 280;

// Status values considered "the one in motion" — used to pick which row to scroll into view.
const ACTIVE_FILE_STATUSES = new Set(["uploading", "retrying", "paused"]);

export const UploadFileRows = ({ uploadId, compact = false, showUploadedInKb = false }) => {
  const files = useChunkedUploadFiles(uploadId);
  return (
    <UploadFileRowsView files={files} compact={compact} showUploadedInKb={showUploadedInKb} />
  );
};

// Presentational half: takes the per-file snapshot as a prop. Split out so the
// Tutorials demo card can render the exact same rows from fixtures instead of
// the live ChunkedUploadService map — one source of truth for status labels.
export const UploadFileRowsView = ({ files, compact = false, showUploadedInKb = false }) => {
  const scrollRef = useRef(null);
  const activeRowRef = useRef(null);

  // Track the active file's sourceFileId so the effect only re-fires when it actually changes,
  // not on every progress tick — keeps the scroll from fighting the user's manual scroll.
  const activeFileId = files?.find((f) => ACTIVE_FILE_STATUSES.has(f.status))?.sourceFileId;

  useEffect(() => {
    if (!compact) return;
    if (!activeRowRef.current || !scrollRef.current) return;
    activeRowRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [compact, activeFileId]);

  if (!files || files.length === 0) return null;

  const content = (
    <Stack spacing={0}>
      {files.map((f, index) => {
        const total = f.sizeBytes || 0;
        const loaded = Math.min(total, f.loaded || 0);
        const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
        const isDone = f.status === "uploaded";
        const isFailed = f.status === "failed";
        const isQueued = f.status === "queued";
        const isPaused = f.status === "paused";
        const isActive = f.sourceFileId === activeFileId;

        return (
          <Box
            key={f.sourceFileId}
            ref={isActive ? activeRowRef : undefined}
            sx={{ fontSize: 12, minWidth: 0 }}
          >
            <Box sx={{ py: 1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  alignItems: "center",
                  columnGap: 1,
                  mb: "4px",
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="caption"
                  title={f.originalName}
                  sx={{
                    minWidth: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                  }}
                >
                  {f.originalName}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    textAlign: "right",
                  }}
                >
                  {formatLoadedOverTotal(loaded, total, showUploadedInKb)}
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={pct}
                // Matches the main-card treatment (2026-08-04 UX pass):
                // paused per-file rows go warning-orange too so the
                // paused state reads at a glance even in the expanded
                // file list, not only via the "Paused" text label.
                color={
                  isFailed
                    ? "error"
                    : isDone
                    ? "success"
                    : isPaused
                    ? "warning"
                    : "primary"
                }
                // Pin the track background to a translucent primary so the
                // unfilled portion looks the same whether the file is
                // uploading, paused, or failed — only the filled bar
                // reflects the state, matching the main-card bar.
                sx={{
                  backgroundColor: (theme) =>
                    `${theme.palette.primary.main}44`,
                  ...(isQueued ? { opacity: 0.35 } : {}),
                }}
              />

              {isFailed && f.error ? (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{
                    display: "block",
                    mt: "4px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={f.error}
                >
                  {f.error}
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.7,
                    display: "block",
                    mt: "4px",
                  }}
                >
                  {STATUS_LABEL[f.status] || f.status}
                </Typography>
              )}
            </Box>

            {index < files.length - 1 && (
              <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
            )}
          </Box>
        );
      })}
    </Stack>
  );

  if (!compact) {
    return <Box sx={{ mt: 1 }}>{content}</Box>;
  }

  return (
    <Box
      ref={scrollRef}
      sx={{
        mt: 1,
        maxHeight: COMPACT_LIST_MAX_HEIGHT_PX,
        overflowY: "auto",
        pr: 0.5,
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(255,255,255,0.18)",
          borderRadius: 3,
        },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      }}
    >
      {content}
    </Box>
  );
};
